import { useBaseForm } from '@pesto/hooks/src/useBaseForm';
import { useMutationWithToast } from '@pesto/ui';
import { Button, Typography } from '@pesto/ui';
import { DropdownInput } from '@pesto/ui/components/DropdownInput';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { TextField } from '@pesto/ui/components/Forms/TextField';
import {
  Sortable,
  SortableContent,
  SortableItem,
  SortableItemHandle,
  SortableOverlay,
} from '@pesto/ui/components/Sortable';
import { Form } from '@pesto/ui/features/Form/Form';
import { PlusIcon } from '@radix-ui/react-icons';
import { GripVertical } from 'lucide-react';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../../types/modal.types';

import { useAddCardTransaction } from '../../../__generated__/rest/api';
import { toNestedJson } from '../../utils/toNestedJson';

enum TransactionType {
  DECLINE = 'DECLINE',
  AUTH = 'AUTH',
  REVERSAL = 'REVERSAL',
  SETTLEMENT = 'SETTLEMENT',
  PURCHASE = 'PURCHASE',
  FEE = 'FEE',
}

const transactionTypeOptions = [
  { value: TransactionType.DECLINE, label: 'Decline' },
  { value: TransactionType.AUTH, label: 'Auth' },
  { value: TransactionType.REVERSAL, label: 'Reversal' },
  { value: TransactionType.SETTLEMENT, label: 'Settlement' },
  { value: TransactionType.PURCHASE, label: 'Purchase' },
  { value: TransactionType.FEE, label: 'Fee' },
];

const keyValuePairSchema = z.object({
  id: z.string(),
  key: z
    .string()
    .min(1, 'Key is required')
    .refine((val) => /^[0-9.]+$/.test(val), {
      message: 'Key must contain only numbers, and dots for nesting',
    }),
  value: z.string().min(1, 'Value is required'),
});

const transactionTypeSchema = z.nativeEnum(TransactionType, {
  required_error: 'Transaction type is required',
});

const formSchema = z.object({
  cardId: z.string().min(1, 'Card is required'),
  transactionType: transactionTypeSchema,
  amountCents: z.number(),
  stan: z.string().optional(),
  merchantName: z.string().optional(),
  originalStan: z.string().optional(),
  keyValuePairs: z.array(keyValuePairSchema).refine(
    (pairs) => {
      const keys = pairs.map((pair) => pair.key);

      return new Set(keys).size === keys.length;
    },
    {
      message: 'Keys must be unique',
    },
  ),
});

type FormValues = z.infer<typeof formSchema>;

type LoanCardOption = {
  id?: string | null;
  lastFour?: string | null;
  status?: string | null;
  ownerName?: string | null;
};

const DEFAULT_FORM_VALUES: FormValues = {
  cardId: '',
  transactionType: TransactionType.DECLINE,
  amountCents: 0,
  keyValuePairs: [],
};

const toCardOption = (card: LoanCardOption) => ({
  value: card.id ?? '',
  label: [
    `•••• ${card.lastFour ?? '????'}`,
    card.ownerName ? `— ${card.ownerName}` : null,
    card.status ? `(${card.status})` : null,
  ]
    .filter(Boolean)
    .join(' '),
});

export const LedgerTransactionForm = ({ payload, api }: ModalProps<LoanModalPayload & { cards: LoanCardOption[] }>) => {
  const { loanId, cards } = payload;
  const cardOptions = cards.map(toCardOption);
  const addCardTransactionMutation = useAddCardTransaction();
  const { handleMutation } = useMutationWithToast();

  const {
    formData,
    formState,
    setValue,
    watch,
    handleSubmit: formHandleSubmit,
  } = useBaseForm<FormValues>({
    schema: formSchema,
    defaultValues: { ...DEFAULT_FORM_VALUES, cardId: cards.length === 1 ? (cards[0].id ?? '') : '' },
  });

  const keyValuePairs = watch('keyValuePairs');

  const handleAddKeyValuePair = () => {
    setValue('keyValuePairs', [...keyValuePairs, { id: String(Date.now()), key: '', value: '' }]);
  };

  const handleRemoveKeyValuePair = (id: string) => {
    setValue(
      'keyValuePairs',
      keyValuePairs.filter((pair) => pair.id !== id),
    );
  };

  const handleKeyValueChange = (id: string, field: 'key' | 'value', value: string) => {
    setValue(
      'keyValuePairs',
      keyValuePairs.map((pair) => (pair.id === id ? { ...pair, [field]: value } : pair)),
    );
  };

  const onSubmit = async (data: FormValues) => {
    const jsonData = toNestedJson(
      keyValuePairs.flatMap(({ key, value }) => (key.trim() === '' ? [] : [{ path: key.split('.'), value }])),
    );

    await handleMutation({
      mutation: () =>
        addCardTransactionMutation.mutateAsync({
          data: {
            loanId,
            cardId: data.cardId,
            transactionType: data.transactionType,
            json: jsonData,
            amountCents: data.amountCents,
            stan: data.stan,
            merchantName: data.merchantName,
            originalStan: data.originalStan,
          },
        }),
      extractSuccess: (result) => !!result?.success,
      onSuccess: () => api.close(),
      debugLabel: 'add-card-transaction',
      toast: {
        success: 'Ledger transaction created successfully',
        error: 'Failed to create ledger transaction',
        loading: 'Creating ledger transaction...',
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Form onSubmit={(event) => void formHandleSubmit(onSubmit)(event)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <DropdownInput
            label="Card"
            options={cardOptions}
            value={formData.cardId}
            onChange={(value) => setValue('cardId', value)}
            placeholder="Select a card"
            required
            error={formState.errors.cardId?.message}
          />
          <DropdownInput
            label="Transaction Type"
            options={transactionTypeOptions}
            value={formData.transactionType}
            onChange={(value) => {
              const parsed = transactionTypeSchema.safeParse(value);

              if (parsed.success) setValue('transactionType', parsed.data);
            }}
            placeholder="Select transaction type"
            required
          />
          <CurrencyField
            label="Amount"
            showCurrencySymbol
            value={formData.amountCents}
            onChange={(value) => setValue('amountCents', value)}
            placeholder="Amount"
          />
          <TextField
            label="Stan (optional)"
            value={formData.stan}
            onChange={(e) => setValue('stan', e.target.value)}
            placeholder="Stan"
          />
          <TextField
            label="Merchant Name (optional)"
            value={formData.merchantName}
            onChange={(e) => setValue('merchantName', e.target.value)}
            placeholder="Merchant Name"
          />
          <TextField
            label="Original Stan (optional)"
            value={formData.originalStan}
            onChange={(e) => setValue('originalStan', e.target.value)}
            placeholder="Original Stan"
          />

          {formState.errors.transactionType && (
            <Typography variant="bodySmall" className="text-danger">
              {formState.errors.transactionType.message}
            </Typography>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Typography variant="body" className="font-medium">
            Custom Fields
          </Typography>
          <Typography variant="bodySmall" className="text-muted-foreground">
            Add key-value pairs using dot notation for nested properties (e.g., user.address.street)
          </Typography>

          <Sortable
            value={keyValuePairs}
            onValueChange={(items) => setValue('keyValuePairs', items)}
            getItemValue={(item) => item.id}
          >
            <SortableContent className="flex flex-col gap-2">
              {keyValuePairs.map((pair, index) => (
                <SortableItem key={pair.id} value={pair.id} className="flex gap-2">
                  <SortableItemHandle asChild>
                    <Button variant="ghost" size="icon" className="mt-8 size-5">
                      <GripVertical className="h-4 w-4" />
                    </Button>
                  </SortableItemHandle>
                  <div className="flex-1">
                    <TextField
                      label="Key"
                      value={pair.key}
                      onChange={(e) => handleKeyValueChange(pair.id, 'key', e.target.value)}
                      placeholder="Field name"
                      error={formState.errors.keyValuePairs?.[index]?.key?.message}
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      label="Value"
                      value={pair.value}
                      onChange={(e) => handleKeyValueChange(pair.id, 'value', e.target.value)}
                      placeholder="Field value"
                      error={formState.errors.keyValuePairs?.[index]?.value?.message}
                    />
                  </div>
                  {keyValuePairs.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      color="destructive"
                      onClick={() => handleRemoveKeyValuePair(pair.id)}
                      className="mt-5"
                    >
                      Remove
                    </Button>
                  )}
                </SortableItem>
              ))}
            </SortableContent>
            <SortableOverlay>
              <div className="bg-primary/10 size-full rounded-md" />
            </SortableOverlay>
          </Sortable>

          {formState.errors.keyValuePairs && (
            <Typography variant="bodySmall" className="text-destructive">
              {formState.errors.keyValuePairs.message}
            </Typography>
          )}

          <Button type="button" variant="outline" onClick={handleAddKeyValuePair} className="mt-2 w-fit">
            <PlusIcon className="mr-2 size-4" /> Add Field
          </Button>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="submit"
            loading={addCardTransactionMutation.isPending}
            disabled={addCardTransactionMutation.isPending}
          >
            Create Transaction
          </Button>
        </div>
      </Form>
    </div>
  );
};
